import { Outlet, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bike, ClipboardList, User, Phone, MessageCircle, MapPin, HeartHandshake, Home as HomeIcon, LogIn } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeSwitcher from "@/components/ThemeSwitcher";
import BrandMark from "@/components/BrandMark";
import SiteFooter from "@/components/SiteFooter";
import { useClientTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { BRAND_TAGLINE_IT, BRAND_TAGLINE_EN, BRAND_PHONE, BRAND_PHONE_DISPLAY, BRAND_WHATSAPP, BRAND_WHATSAPP_DISPLAY } from "@/lib/brand";

export default function CustomerLayout() {
  const { t, i18n } = useTranslation();
  const isEN = i18n.language === "en";
  const { theme } = useClientTheme();
  const { user } = useAuth();
  // Iter 49 — /home, /catalog, /scooter/:id are public. Bottom-nav adapts:
  // logged-in customers see "My rentals" + "Profile"; guests see "Login".
  const isGuest = !user || user === false;
  const navItems = isGuest
    ? [
        { to: "/home", label: isEN ? "Home" : "Home", icon: HomeIcon, testId: "nav-home" },
        { to: "/catalog", label: t("nav.catalog"), icon: Bike, testId: "nav-catalog" },
        { to: "/login", label: isEN ? "Login" : "Accedi", icon: LogIn, testId: "nav-login" },
      ]
    : [
        { to: "/home", label: isEN ? "Home" : "Home", icon: HomeIcon, testId: "nav-home" },
        { to: "/catalog", label: t("nav.catalog"), icon: Bike, testId: "nav-catalog" },
        { to: "/my-rentals", label: t("nav.my_rentals"), icon: ClipboardList, testId: "nav-my-rentals" },
        { to: "/profile", label: t("nav.profile"), icon: User, testId: "nav-profile" },
      ];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 pb-20" data-theme={theme} data-testid="customer-root">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-zinc-950/70 border-b border-white/5">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-3 px-5 py-4">
          <div className="flex items-center gap-3 min-w-0">
            <NavLink to="/home" className="hover:opacity-90 transition" data-testid="header-home-link">
              <BrandMark size="md" />
            </NavLink>
            <a href={`tel:${BRAND_PHONE}`}
               className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFD700]/[0.08] border border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/[0.15] transition group"
               data-testid="header-help-cta">
              <HeartHandshake className="w-4 h-4 group-hover:scale-110 transition" />
              <span className="text-[12px] font-medium leading-tight">
                {isEN
                  ? "Need help? We're a small family business — we still answer the phone."
                  : "Serve aiuto? Siamo una piccola azienda — rispondiamo ancora al telefono."}
              </span>
            </a>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="hidden lg:inline text-[10px] text-zinc-500 uppercase tracking-widest">
              {isEN ? BRAND_TAGLINE_EN : BRAND_TAGLINE_IT}
            </span>
            <ThemeSwitcher />
            <LanguageSwitcher />
          </div>
        </div>
        {/* Mobile-only short version of the help CTA */}
        <a href={`tel:${BRAND_PHONE}`}
           className="md:hidden flex items-center justify-center gap-2 px-5 py-2 bg-[#FFD700]/[0.06] border-t border-[#FFD700]/30 text-[#FFD700] text-[12px] font-medium"
           data-testid="header-help-cta-mobile">
          <HeartHandshake className="w-3.5 h-3.5" />
          {isEN ? "Need help? Call us — we still answer the phone." : "Serve aiuto? Chiamaci — rispondiamo ancora al telefono."}
        </a>
        {/* Contact strip — always visible to clients */}
        <div className="border-t border-white/5 bg-zinc-950/40">
          <div className="max-w-5xl mx-auto flex items-center justify-between gap-2 px-5 py-2 text-xs">
            <div className="hidden sm:flex items-center gap-1.5 text-zinc-500">
              <MapPin className="w-3.5 h-3.5" />
              <span>{t("contact.address")}</span>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <a href={`tel:${BRAND_PHONE}`}
                 className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-zinc-800 hover:border-[#FFD700]/50 hover:text-[#FFD700] text-zinc-300 transition-colors"
                 data-testid="header-contact-phone">
                <Phone className="w-3.5 h-3.5" />
                <span className="font-medium">{BRAND_PHONE_DISPLAY}</span>
              </a>
              <a href={`https://wa.me/${BRAND_WHATSAPP.replace(/[^0-9]/g, "")}`}
                 target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#25D366]/15 border border-[#25D366]/40 hover:bg-[#25D366]/25 text-[#25D366] font-medium transition-colors"
                 data-testid="header-contact-whatsapp">
                <MessageCircle className="w-3.5 h-3.5" />
                <span>{BRAND_WHATSAPP_DISPLAY}</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <Outlet />
      </main>

      <SiteFooter />

      <nav className="fixed bottom-0 left-0 right-0 z-40 backdrop-blur-xl bg-zinc-950/85 border-t border-white/10">
        <div className={`max-w-5xl mx-auto grid ${isGuest ? "grid-cols-3" : "grid-cols-4"} px-2 py-1.5`}>
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink key={item.to} to={item.to} data-testid={item.testId}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-0.5 py-1.5 rounded-lg transition-all ${
                    isActive
                      ? "text-[#FFD700] bg-[#FFD700]/[0.06]"
                      : "text-zinc-400 hover:text-zinc-100"
                  }`
                }>
                <Icon className="w-5 h-5" strokeWidth={2} />
                <span className="text-[10px] font-semibold tracking-tight">{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
